<template>
  <div class="seller-comments" :class="{ 'seller-comments-panel': panel }">
    <button type="button" class="seller-comments-toggle" @click="expanded = !expanded">
      <span class="seller-comments-label">
        Commentaires<span v-if="comments.length"> ({{ comments.length }})</span>
      </span>
      <i :class="expanded ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
    </button>

    <div v-show="expanded">
    <p v-if="loading" class="seller-comments-empty">Chargement...</p>

    <ul v-if="comments.length" class="seller-comments-list" @wheel.stop>
      <li v-for="item in comments" :key="item.id_vendeur_commentaire">
        <div class="seller-comments-meta">
          <span>{{ authorName(item) }} · {{ formatDateTime(item.date_creation) }}</span>
          <button
            v-if="item.canDelete"
            type="button"
            title="Supprimer le commentaire"
            :disabled="saving"
            @click="remove(item)"
          >
            <i class="fas fa-trash-alt"></i>
          </button>
        </div>
        <p>{{ item.commentaire }}</p>
      </li>
    </ul>
    <p v-else-if="!loading" class="seller-comments-empty">Aucun commentaire.</p>

    <textarea
      v-model="draft"
      :rows="panel ? 1 : 2"
      maxlength="256"
      placeholder="Écrivez votre commentaire..."
    ></textarea>
    <div class="seller-comments-actions">
      <span>{{ draft.trim().length }}/256</span>
      <button type="button" :disabled="saving || !draft.trim()" @click="save">
        Enregistrer
      </button>
    </div>
    </div>

    <ConfirmModal
      :show="pendingDelete != null"
      title="Supprimer le commentaire"
      message="Supprimer ce commentaire ?"
      confirm-text="Supprimer"
      confirm-button-class="bg-red-600 hover:bg-red-700 focus:ring-red-500"
      :confirm-loading="saving"
      loading-text="Suppression…"
      @confirm="confirmRemove"
      @cancel="pendingDelete = null"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from "vue";
import { fetchRest, getApiBaseUrl } from "@/config/api";
import { ConfirmModal } from "@/components";
import { useNotification } from "@/composables/useNotification";

interface SellerComment {
  id_vendeur_commentaire: number;
  commentaire: string;
  date_creation: string;
  prenom?: string | null;
  nom?: string | null;
  canDelete?: boolean;
}

const props = defineProps<{
  idVendeur: number;
  panel?: boolean;
}>();

const { success, error: notifyError } = useNotification();
const comments = ref<SellerComment[]>([]);
const expanded = ref(false);
const draft = ref("");
const loading = ref(false);
const saving = ref(false);
const pendingDelete = ref<SellerComment | null>(null);

const authorName = (item: SellerComment) =>
  `${item.prenom || ""} ${item.nom || ""}`.trim() || "Admin";

const formatDateTime = (value?: string) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const load = async () => {
  if (!props.idVendeur) {
    comments.value = [];
    return;
  }
  loading.value = true;
  try {
    const response = await fetchRest(
      `${getApiBaseUrl()}/api/admin-efp/vendeurs/commentaires/get`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ idVendeur: props.idVendeur }),
      },
    );
    const data = await response.json();
    comments.value = Array.isArray(data) ? data : [];
  } catch {
    notifyError("Impossible de charger les commentaires.");
    comments.value = [];
  } finally {
    loading.value = false;
  }
};

const save = async () => {
  const commentaire = draft.value.trim();
  if (!commentaire || saving.value) return;
  saving.value = true;
  try {
    const response = await fetchRest(
      `${getApiBaseUrl()}/api/admin-efp/vendeurs/commentaires/save`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ idVendeur: props.idVendeur, commentaire }),
      },
    );
    const created = await response.json();
    if (created?.id_vendeur_commentaire) {
      comments.value = [...comments.value, created];
      draft.value = "";
      success("Commentaire enregistré.");
    } else {
      notifyError("Le commentaire n'a pas été enregistré.");
    }
  } catch {
    notifyError("Impossible d'enregistrer le commentaire.");
  } finally {
    saving.value = false;
  }
};

const remove = (item: SellerComment) => {
  if (!item.canDelete || saving.value) return;
  pendingDelete.value = item;
};

const confirmRemove = async () => {
  const item = pendingDelete.value;
  if (!item || saving.value) return;
  saving.value = true;
  try {
    const response = await fetchRest(
      `${getApiBaseUrl()}/api/admin-efp/vendeurs/commentaires/delete`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          idVendeur: props.idVendeur,
          idCommentaire: item.id_vendeur_commentaire,
        }),
      },
    );
    const deleted = await response.json();
    if (deleted === true) {
      comments.value = comments.value.filter(
        (comment) => comment.id_vendeur_commentaire !== item.id_vendeur_commentaire,
      );
      pendingDelete.value = null;
      success("Commentaire supprimé.");
    } else {
      notifyError("Ce commentaire ne peut pas être supprimé.");
      pendingDelete.value = null;
    }
  } catch {
    notifyError("Impossible de supprimer le commentaire.");
    pendingDelete.value = null;
  } finally {
    saving.value = false;
  }
};

watch(() => props.idVendeur, () => void load(), { immediate: true });
</script>

<style scoped>
.seller-comments {
  flex: 1;
  min-width: 220px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.seller-comments-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  padding: 0;
  border: 0;
  background: none;
  cursor: pointer;
  color: #c2410c;
}

.seller-comments-label {
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.2px;
  text-transform: uppercase;
  color: #c2410c;
}

.seller-comments-empty,
.seller-comments-meta,
.seller-comments-actions {
  font-size: 11px;
  color: #6b7280;
}

.seller-comments-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: 180px;
  overflow-y: auto;
  overscroll-behavior: contain;
  margin: 0;
  padding: 0;
  list-style: none;
}

.seller-comments-list li {
  border: 1px solid #fed7aa;
  border-radius: 6px;
  background: #fff;
  padding: 4px 6px;
}

.seller-comments-list p {
  margin: 2px 0 0;
  font-size: 12px;
  color: #111;
  white-space: pre-wrap;
  word-break: break-word;
}

.seller-comments-meta {
  display: flex;
  justify-content: space-between;
  gap: 8px;
}

.seller-comments-meta button {
  border: 0;
  background: none;
  color: #ef4444;
  cursor: pointer;
  padding: 0;
}

.seller-comments textarea {
  width: 100%;
  resize: none;
  border: 1px solid #fdba74;
  border-radius: 6px;
  background: #fff;
  padding: 6px 8px;
  font-size: 12px;
}

.seller-comments-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.seller-comments-actions button {
  border: 0;
  border-radius: 6px;
  background: #ea580c;
  color: white;
  font-size: 11px;
  padding: 4px 8px;
  cursor: pointer;
}

.seller-comments-actions button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.seller-comments-panel {
  width: 100%;
  min-width: 0;
  gap: 6px;
}

.seller-comments-panel .seller-comments-label {
  font-size: 13px;
  letter-spacing: 0;
  text-transform: none;
}

.seller-comments-panel .seller-comments-empty,
.seller-comments-panel .seller-comments-meta,
.seller-comments-panel .seller-comments-actions,
.seller-comments-panel .seller-comments-list p,
.seller-comments-panel textarea {
  font-size: 13px;
}

.seller-comments-panel .seller-comments-list {
  max-height: 120px;
}

.seller-comments-panel .seller-comments-actions button {
  font-size: 12px;
  padding: 4px 10px;
}
</style>
